import { useState } from "react";
import {
  Button,
  Callout,
  FormGroup,
  InputGroup,
  Intent,
} from "@blueprintjs/core";
import type { ExtendedUser } from "./UserForm";

interface PasswordFormProps {
  content: ExtendedUser;
  errors: Record<string, string>;
  onChange: (patch: Partial<ExtendedUser>) => void;
}

export const PasswordForm = ({
  content,
  onChange,
  errors,
}: PasswordFormProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const LOWER = "abcdefghijklmnopqrstuvwxyz";
  const NUMBERS = "0123456789";
  const SYMBOLS = "!@#$%^&*()_+~`|}{[]:;?><,./-=";

  const ALL = UPPER + LOWER + NUMBERS + SYMBOLS;

  const getRandomChar = (charset: string) => {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return charset[array[0] % charset.length];
  };

  const generateSecurePassword = (length = 12): string => {
    if (length < 10) {
      throw new Error("Password length must be at least 10 characters");
    }

    const passwordChars = [
      getRandomChar(UPPER),
      getRandomChar(LOWER),
      getRandomChar(NUMBERS),
      getRandomChar(SYMBOLS),
    ];

    while (passwordChars.length < length) {
      passwordChars.push(getRandomChar(ALL));
    }

    for (let i = passwordChars.length - 1; i > 0; i--) {
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      const j = array[0] % (i + 1);
      [passwordChars[i], passwordChars[j]] = [
        passwordChars[j],
        passwordChars[i],
      ];
    }

    return passwordChars.join("");
  };

  const handleGenerate = () => {
    const pwd = generateSecurePassword(12);
    onChange({ password: pwd, confirmPassword: pwd });
  };

  const renderVisibilityButton = () => (
    <Button
      icon={showPassword ? "eye-off" : "eye-open"}
      minimal
      onClick={() => setShowPassword(!showPassword)}
    />
  );

  return (
    <section className="user-access-form flex w-[720px] max-w-full flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase text-slate-500">
            Security
          </div>
          <h3 className="mt-1 text-base font-semibold text-slate-950">
            Temporary password
          </h3>
        </div>
        <Button
          className="!rounded-xl"
          icon="refresh"
          onClick={handleGenerate}
          small
          text="Generate"
        />
      </header>

      <Callout
        className="!rounded-xl !border !border-amber-200 !bg-amber-50 !text-amber-900"
        icon="info-sign"
        intent={Intent.WARNING}
      >
        <strong>Shown once after creation.</strong>
      </Callout>

      <div className="grid gap-4 md:grid-cols-2">
        <FormGroup
          helperText={errors.password}
          intent={errors.password ? Intent.DANGER : Intent.NONE}
          label="Password"
          labelFor="password-input"
        >
          <InputGroup
            id="password-input"
            intent={errors.password ? Intent.DANGER : Intent.NONE}
            name="password"
            onChange={(e) => onChange({ password: e.target.value })}
            placeholder="Password"
            rightElement={renderVisibilityButton()}
            type={showPassword ? "text" : "password"}
            value={content?.password}
          />
        </FormGroup>

        <FormGroup
          helperText={errors.confirmPassword}
          intent={errors.confirmPassword ? Intent.DANGER : Intent.NONE}
          label="Confirm password"
          labelFor="confirm-password-input"
        >
          <InputGroup
            id="confirm-password-input"
            intent={errors.confirmPassword ? Intent.DANGER : Intent.NONE}
            name="confirmPassword"
            onChange={(e) => onChange({ confirmPassword: e.target.value })}
            placeholder="Confirm password"
            rightElement={renderVisibilityButton()}
            type={showPassword ? "text" : "password"}
            value={content?.confirmPassword}
          />
        </FormGroup>
      </div>
    </section>
  );
};
